import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Empty, Input, Select, Spin, Table, Tooltip, Typography, message } from "antd";
import { BarChartOutlined, SearchOutlined, ArrowUpOutlined, ArrowDownOutlined, FileExcelOutlined, FilePdfOutlined, MailOutlined } from "@ant-design/icons";
import { ResponsiveContainer, BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip as ChartTooltip, PieChart, Pie, Cell, LabelList } from "recharts";
import dayjs from "dayjs";
import { BASE_URL } from "../services/BaseUrl";
import "../css/sm-art-common.css";

const API_ROOT = new URL("/index.php/backlog/", BASE_URL.BASE).toString();
const fmt = (n) => n === null || n === undefined || n === "" || !Number.isFinite(Number(n)) ? "—" : Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const pcs = (n) => Number.isFinite(Number(n)) ? Number(n) : 0;
const PIE_COLORS = ["#2563eb", "#0d9488", "#f59e0b", "#7c3aed", "#ef4444", "#0284c7", "#84cc16", "#db2777"];
const field = (name, key, width = 135, align = "center") => ({ title: name, dataIndex: key, key, width, align, render: (v) => fmt(v) });
const nameLabel = (value) => {
  const raw = String(value ?? "—").trim();
  return raw.toLowerCase().startsWith("unit") ? raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase() : raw;
};
// Material percentages are presentation-only, calculated from live API values.
const materialParts = (value, row) => {
  if (value === null || value === undefined || value === "" || !Number.isFinite(Number(value))) {
    return { quantity: fmt(value), percent: null };
  }
  const total = pcs(row?.backlog_with_pm) + pcs(row?.backlog_without_pm) + pcs(row?.backlog_late_material);
  return {
    quantity: fmt(value),
    percent: total > 0 ? `${Math.round(Number(value) / total * 100)}%` : null,
  };
};
const materialText = (value, row) => {
  const { quantity, percent } = materialParts(value, row);
  return percent == null ? quantity : `${quantity}  ${percent}`;
};
const materialBadgeTone = (percent) => {
  const n = Number.parseFloat(percent);
  return n >= 70 ? "high" : n >= 10 ? "medium" : "low";
};
const materialDisplay = (value, row) => {
  const { quantity, percent } = materialParts(value, row);
  return <span className="fba-material-display"><strong className="fba-material-quantity">{quantity}</strong>{percent !== null && <span className={`fba-material-percent fba-material-percent--${materialBadgeTone(percent)}`}>{percent}</span>}</span>;
};
const changeText = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `${fmt(n)}${n > 0 ? "▲" : n < 0 ? "▼" : ""}`;
};
const remarksText = (value) => value === null || value === undefined || String(value).trim() === "—" ? "" : String(value);
const changeColor = (value) => Number(value) > 0 ? "#dc2626" : Number(value) < 0 ? "#15803d" : "#334155";
const apiUrl = (endpoint, date, refresh, other = {}) => {
  const url = new URL(endpoint, API_ROOT);
  url.searchParams.set("date", date);
  if (refresh) url.searchParams.set("refresh", "1");
  Object.entries(other).forEach(([k, v]) => url.searchParams.set(k, String(v)));
  return url.toString();
};
async function getReport(endpoint, date, refresh, signal, params) {
  const response = await fetch(apiUrl(endpoint, date, refresh, params), { signal });
  if (!response.ok) throw new Error(`${endpoint}: HTTP ${response.status}`);
  const json = await response.json();
  if (json?.status !== true || !json?.data || Array.isArray(json.data)) throw new Error(json?.message || `${endpoint}: invalid response`);
  return json.data;
}

function ChartPanel({ title, children }) {
  return <section className="fba-chart-panel"><div className="fba-chart-head"><strong>{title}</strong></div>{children}</section>;
}
function VerticalBarPanel({ title, data, labelKey, color }) {
  const list = Array.isArray(data) ? data : [];
  // MIS reference: vertical columns, value labels, all buyers/units readable.
  // Scrolling horizontally avoids compressing dozens of buyer labels.
  const minWidth = Math.max(850, list.length * (labelKey === "buyer" ? 63 : 53));
  return <ChartPanel title={title}>
    {list.length ? <div className="fba-chart-scroll"><div style={{ height: 375, minWidth }}><ResponsiveContainer width="100%" height="100%">
      <BarChart data={list} margin={{ top: 26, right: 18, bottom: 104, left: 10 }} barCategoryGap="22%">
        <CartesianGrid vertical={false} stroke="#edf1f5" />
        <XAxis type="category" dataKey={labelKey} interval={0} height={102} tickMargin={9} angle={-53} textAnchor="end" tick={{ fontSize: 10, fill: "#52627a" }} />
        <YAxis type="number" width={46} tick={{ fontSize: 10 }} tickFormatter={fmt} />
        <ChartTooltip formatter={(value, _name, context) => [`${fmt(value)} thousand (${fmt(context?.payload?.total_backlog)} pcs)`, "Backlog"]} />
        <Bar dataKey="backlog_thousands" name="Backlog" fill={color} maxBarSize={35} radius={[3, 3, 0, 0]}>
          <LabelList dataKey="backlog_thousands" position="top" formatter={(value) => fmt(value)} style={{ fontSize: 9, fill: "#405574" }} />
        </Bar>
      </BarChart>
    </ResponsiveContainer></div></div> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No chart data" />}
  </ChartPanel>;
}
function HorizontalBuyerBarPanel({ data }) {
  const list = Array.isArray(data) ? data : [];
  const chartHeight = Math.max(230, list.length * 37 + 36);
  return <ChartPanel title="Buyer-wise Finishing Backlog">
    {list.length ? <div className="fba-buyer-scroll" tabIndex={0} aria-label="Scrollable buyer-wise backlog chart">
      <div style={{ height: chartHeight, minWidth: 590 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={list} layout="vertical" margin={{ top: 9, right: 92, bottom: 12, left: 4 }} barCategoryGap="28%">
            <CartesianGrid horizontal={false} stroke="#e8edf6" />
            <XAxis type="number" tick={{ fontSize: 10, fill: "#62728a" }} tickFormatter={fmt} />
            <YAxis type="category" dataKey="buyer" width={235} interval={0} tick={{ fontSize: 11, fill: "#243b59" }} />
            <ChartTooltip formatter={(value, _name, context) => [`${fmt(value)} thousand (${fmt(context?.payload?.total_backlog)} pcs)`, "Backlog"]} labelFormatter={(label) => `Buyer: ${label}`} />
            <Bar dataKey="backlog_thousands" name="Backlog" fill="#2563eb" radius={[0, 5, 5, 0]} maxBarSize={23}>
              <LabelList dataKey="backlog_thousands" position="right" formatter={fmt} style={{ fontSize: 11, fontWeight: 600, fill: "#1d467e" }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No buyer chart data" />}
  </ChartPanel>;
}
// PDF-inspired layered, elliptical 3D-style buyer pie chart.
// Recharts renders 2D SVG; stacked shaded layers simulate depth.
const shadePieColor = (hex, factor = 0.7) => {
  const raw = hex.replace("#", "");
  const channels = [0, 2, 4].map((index) =>
    Math.round(parseInt(raw.slice(index, index + 2), 16) * factor)
  );
  return `rgb(${channels.join(", ")})`;
};

function BuyerPiePanel({ chart }) {
  const slices = Array.isArray(chart?.slices)
    ? chart.slices.filter((slice) => pcs(slice.backlog_thousands) > 0)
    : [];
  const depthLayers = 9;

  return (
    <section className="fba-pie-card fba-pie-card-3d">
      <div className="fba-pie-title">
        <strong>Unit {chart.unit}</strong>
        <span>{fmt(chart.days_wip)} days WIP · {fmt(chart.total_backlog)} pcs</span>
      </div>
      {slices.length ? (
        <>
          <div className="fba-pie-image fba-pie-image-3d">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                {Array.from({ length: depthLayers }, (_, layer) => (
                  <Pie
                    key={`depth-${layer}`}
                    data={slices}
                    dataKey="backlog_thousands"
                    nameKey="buyer"
                    cx="50%"
                    cy={`${49 + (depthLayers - layer) * 1.25}%`}
                    outerRadius="69%"
                    innerRadius={0}
                    paddingAngle={0}
                    stroke="none"
                    isAnimationActive={false}
                    legendType="none"
                    tooltipType="none"
                  >
                    {slices.map((slice, i) => (
                      <Cell
                        key={`depth-${layer}-${slice.buyer}-${i}`}
                        fill={shadePieColor(PIE_COLORS[i % PIE_COLORS.length], 0.62 + layer * 0.018)}
                        stroke="none"
                      />
                    ))}
                  </Pie>
                ))}
                <Pie
                  data={slices}
                  dataKey="backlog_thousands"
                  nameKey="buyer"
                  cx="50%"
                  cy="49%"
                  outerRadius="69%"
                  innerRadius={0}
                  paddingAngle={0}
                  stroke="#ffffff"
                  strokeWidth={0.7}
                  isAnimationActive={false}
                >
                  {slices.map((slice, i) => (
                    <Cell key={`top-${slice.buyer}-${i}`} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <ChartTooltip
                  allowEscapeViewBox={{ x: false, y: false }}
                  wrapperStyle={{ zIndex: 1000, pointerEvents: "none" }}
                  contentStyle={{ borderRadius: 8, padding: "8px 10px", fontSize: 12, boxShadow: "0 8px 24px rgba(0,0,0,.15)", maxWidth: 205, width: "max-content", whiteSpace: "normal", overflowWrap: "anywhere" }}
                  formatter={(value, _name, item) => [
                    `${fmt(value)} thousand (${fmt(item?.payload?.percent)}%)`,
                    item?.payload?.buyer || "Backlog",
                  ]}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="fba-pie-legend fba-pie-legend-3d">
            {slices.map((slice, i) => (
              <div key={`${slice.buyer}-${i}`}>
                <i style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                <Tooltip title={`${slice.buyer} — ${fmt(slice.percent)}%`} placement="top"><span title={slice.buyer}>{slice.buyer}</span></Tooltip>
                <strong>{fmt(slice.percent)}%</strong>
              </div>
            ))}
          </div>
        </>
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No buyer data" />
      )}
    </section>
  );
}

// Route: <FinishingBacklogAnalytics userdata={userdata} />
// App authentication already passes parsedData.data as userdata.
function reportUserId(userdata) {
  const user = userdata?.data ?? userdata;
  const id = user?.su_id ?? user?.user_id ?? user?.userId ?? user?.id;
  return id === null || id === undefined || String(id).trim() === ""
    ? null
    : String(id);
}

export default function FinishingBacklogAnalytics({ userdata }) {
  const date = dayjs().format("YYYY-MM-DD");
  const [summary, setSummary] = useState(null);
  const [bars, setBars] = useState(null);
  const [pies, setPies] = useState(null);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [trendFilter, setTrendFilter] = useState("all");
  const [page, setPage] = useState(1);
  const requestRef = useRef(0);
  const abortRef = useRef(null);
  const dashboardRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const [sendingMail, setSendingMail] = useState(false);
  const load = useCallback(async (selectedDate, refresh = false) => {
    const request = ++requestRef.current;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setErrors([]);
    const endpoints = ["backlog_summary", "backlog_bar_chart", "backlog_pie_chart"];
    const results = await Promise.allSettled(endpoints.map((e) => getReport(e, selectedDate, refresh, controller.signal, e === "backlog_pie_chart" ? { top: 3 } : {})));
    if (controller.signal.aborted || request !== requestRef.current) return;
    const failures = results.flatMap((r, i) => r.status === "rejected" ? [`${endpoints[i]}: ${r.reason?.message || "Failed"}`] : []);
    setSummary(results[0].status === "fulfilled" ? results[0].value : null);
    setBars(results[1].status === "fulfilled" ? results[1].value : null);
    setPies(results[2].status === "fulfilled" ? results[2].value : null);
    setErrors(failures);
    setLoading(false);
  }, []);
  useEffect(() => { load(date); return () => { ++requestRef.current; abortRef.current?.abort(); }; }, [date, load]);
  const units = Array.isArray(summary?.units) ? summary.units.filter((unit) => unit && String(unit.unit ?? unit.unit_code ?? "").trim() !== "") : [];
  // Reference MIS order: sort by Days WIP descending; use workbook's unit order for ties.
  const referenceUnitOrder = ["30", "40", "20", "41", "61", "26", "12", "60", "32", "14", "11", "42", "26E", "63", "6", "4", "47", "36"];
  const displayUnits = useMemo(() => [...units].sort((a, b) => {
    const dayDiff = pcs(b.days_wip) - pcs(a.days_wip);
    if (dayDiff) return dayDiff;
    const ai = referenceUnitOrder.indexOf(String(a.unit ?? a.unit_code ?? "").replace(/^unit\s*/i, "").toUpperCase());
    const bi = referenceUnitOrder.indexOf(String(b.unit ?? b.unit_code ?? "").replace(/^unit\s*/i, "").toUpperCase());
    return (ai < 0 ? 999 : ai) - (bi < 0 ? 999 : bi);
  }), [units]);
  const filtered = useMemo(() => displayUnits.filter((u) => {
    const match = !search.trim() || `${u.unit ?? ""} ${u.unit_code ?? ""} ${u.remarks ?? ""}`.toLowerCase().includes(search.trim().toLowerCase());
    return match && (trendFilter === "all" || String(u.trend || "same") === trendFilter);
  }), [displayUnits, search, trendFilter]);
  // Excel reference: eight visible columns, with a grouped Finishing Backlog heading.
  const columns = [
    { title: "Unit No.", align: "center", dataIndex: "unit", key: "unit", width: 105, fixed: "left", render: (v, row) => <strong>{nameLabel(v ?? row.unit_code)}</strong> },
    { ...field("Finishing capacity In Pcs/day", "capacity_per_day", 165), render: (v) => <strong className="fba-capacity-value">{fmt(v)}</strong> },
    { title: "Finishing Backlog", key: "finishing_backlog", children: [
      { ...field("With Trims", "backlog_with_pm", 145), render: (v, row) => materialDisplay(v, row) },
      { ...field("Without Trims", "backlog_without_pm", 155), render: (v, row) => materialDisplay(v, row) },
      { ...field("Late Receipt of Trims", "backlog_late_material", 185), render: (v, row) => materialDisplay(v, row) },
      { ...field("Total", "total_backlog", 145), render: (v) => <strong>{fmt(v)}</strong> },
    ] },
    { title: "Compare to previous day Backlog Increase /Decrease", dataIndex: "change_vs_previous", key: "change_vs_previous", width: 270, align: "center", render: (v) => <span style={{ color: changeColor(v), fontWeight: 700, whiteSpace: "nowrap" }}>{changeText(v)}</span> },
    { title: "No of days WIP", dataIndex: "days_wip", key: "days_wip", width: 135, align: "center", render: (v, r) => <Tooltip title={r.days_wip_exact == null ? "" : `Exact: ${fmt(r.days_wip_exact)}`}><strong>{fmt(v)}</strong></Tooltip> },
    { title: "Remarks", dataIndex: "remarks", key: "remarks", width: 305, align: "center", render: (v, r) => r.error ? <span className="fba-error-cell">{r.error}</span> : remarksText(v) },
  ];
  const reportColumns = ["Unit No.", "Finishing capacity In Pcs/day", "With Trims", "Without Trims", "Late Receipt of Trims", "Total", "Compare to previous day Backlog Increase /Decrease", "No of days WIP", "Remarks"];
  const reportRows = displayUnits.map((u) => [nameLabel(u.unit ?? u.unit_code ?? ""), u.capacity_per_day, materialText(u.backlog_with_pm, u, "with"), materialText(u.backlog_without_pm, u, "without"), materialText(u.backlog_late_material, u), u.total_backlog, changeText(u.change_vs_previous), u.days_wip, remarksText(u.error || u.remarks)]);
  // Export total is taken from the same backend summary used by the dashboard.
  // Do not copy the reference workbook's inconsistent material subtotal.
  const reportTotalRow = ["Total", summary?.total?.capacity_per_day ?? 0, materialText(summary?.total?.backlog_with_pm, summary?.total), materialText(summary?.total?.backlog_without_pm, summary?.total), materialText(summary?.total?.backlog_late_material, summary?.total), summary?.total?.total_backlog ?? 0, changeText(summary?.total?.change_vs_previous), summary?.total?.days_wip ?? "", ""];
  const reportHeading = "Daily Finishing Backlog MIS -Knit Division";
  const exportExcel = async () => {
    if (!units.length) return message.warning("No MIS data available");
    try {
      setExporting(true);
      const XLSX = await import("xlsx");
      const sheet = XLSX.utils.aoa_to_sheet([
        [reportHeading],
        [`Date: ${dayjs(date).format("DD/MM/YYYY")}`],
        [],
        ["Unit No.", "Finishing capacity In Pcs/day", "Finishing Backlog", "", "", "", "Compare to previous day Backlog Increase /Decrease", "No of days WIP", "Remarks"],
        ["", "", "With Trims", "Without Trims", "Late Receipt of Trims", "Total", "", "", ""],
        ...reportRows,
        reportTotalRow,
      ]);
      sheet["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 8 } },
        { s: { r: 3, c: 0 }, e: { r: 4, c: 0 } },
        { s: { r: 3, c: 1 }, e: { r: 4, c: 1 } },
        { s: { r: 3, c: 2 }, e: { r: 3, c: 5 } },
        { s: { r: 3, c: 6 }, e: { r: 4, c: 6 } },
        { s: { r: 3, c: 7 }, e: { r: 4, c: 7 } },
        { s: { r: 3, c: 8 }, e: { r: 4, c: 8 } },
      ];
      sheet["!cols"] = [14, 24, 21, 23, 26, 21, 55, 20, 56].map(wch => ({ wch }));
      sheet["!rows"] = [{ hpt: 27 }, { hpt: 20 }, { hpt: 10 }, { hpt: 34 }, { hpt: 26 }];
      for (let r = 5; r <= 5 + reportRows.length; r++) sheet["!rows"][r] = { hpt: 24 };
      // Reference-style totals and number formats, with all 18+ unit rows included.
      const totalExcelRow = reportRows.length + 6;
      for (let c = 1; c <= 6; c++) {
        const cell = sheet[XLSX.utils.encode_cell({ r: totalExcelRow - 1, c })];
        if (cell && typeof cell.v === "number") cell.z = '#,##,##0';
      }
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, sheet, "Finishing Backlog MIS");
      XLSX.writeFile(workbook, `Finishing_Backlog_MIS_${date}.xlsx`);
    } catch (err) { console.error(err); message.error("Excel export failed"); }
    finally { setExporting(false); }
  };
  // Shared generator guarantees that download and mail use identical PDF content.
  const buildReportPdf = async () => {
    if (!summary) throw new Error("Dashboard data is not ready");
      const [{ jsPDF }, autoTableModule] = await Promise.all([
        import("jspdf"), import("jspdf-autotable")
      ]);
      const autoTable = autoTableModule.default || autoTableModule.autoTable;
      // Use a dynamically tall, single PDF sheet (420 mm wide, A3 landscape width).
      // This prevents Buyer-wise/Unit-wise sections from being split at page boundaries.
      const pdfBuyers = Array.isArray(bars?.buyer_wise?.data) ? bars.buyer_wise.data : [];
      const pdfUnits = Array.isArray(bars?.unit_wise?.data) ? bars.unit_wise.data : [];
      const pdfPies = Array.isArray(pies?.charts) ? pies.charts : [];
      const pieHeight = pdfPies.reduce((sum, chart) => {
        const count = Array.isArray(chart.slices) ? chart.slices.length : 0;
        return sum + Math.max(36, 17 + Math.ceil(count / 2) * 5.3) + 5;
      }, 0);
       // Four explicit, content-sized sheets with consistent 420 mm width. Every major chart section starts
       // on its own page; no section shares its page with the next section.
       const safePageHeight = (needed) => Math.min(4800, Math.max(160, Math.ceil(needed)));
       const firstHeight = safePageHeight(27 + 33 + 14 + Math.max(12, pdfBuyers.length * 7.6 + 5) + 32);
       const unitHeight = safePageHeight(27 + 14 + Math.max(12, pdfUnits.length * 6.65 + 5) + 32);
       const piePageHeight = safePageHeight(27 + 14 + pieHeight + 34);
       // Allow enough headroom for a two-row header, optional wrapped text,
       // material badges, all unit rows, and the totals footer.
       const misPageHeight = safePageHeight(27 + 14 + 30 + (reportRows.length + 1) * 10 + 34);
       // jsPDF normalizes dimensions according to page orientation. Explicitly
       // select landscape for short pages, otherwise [420, height] becomes
       // [height, 420] and all chart coordinates overflow the right edge.
       const pageOrientation = (height) => height < 420 ? "landscape" : "portrait";
       const pdf = new jsPDF({ orientation: pageOrientation(firstHeight), unit: "mm", format: [420, firstHeight] });
      const pw = pdf.internal.pageSize.getWidth();
       let ph = pdf.internal.pageSize.getHeight();
      const margin = 13;
      if (Math.abs(pw - 420) > 1) throw new Error(`Unexpected PDF width: ${pw.toFixed(1)} mm`);
      const contentWidth = pw - margin * 2;
       let bottom = ph - 17;
      let y = 27;

      const header = () => {
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(15);
        pdf.setTextColor(23, 43, 74);
        pdf.text("Finishing Backlog Analytics", margin, 14);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(9);
        pdf.setTextColor(100, 115, 136);
        pdf.text(`Report date: ${dayjs(date).format("DD/MM/YYYY")}`, pw - margin, 14, { align: "right" });
        pdf.setDrawColor(224, 232, 242);
        pdf.line(margin, 18, pw - margin, 18);
      };
       const nextPage = (height) => {
         pdf.addPage([420, height], pageOrientation(height));
         const realWidth = pdf.internal.pageSize.getWidth();
         if (Math.abs(realWidth - 420) > 1) {
           throw new Error(`PDF page width mismatch: expected 420 mm, got ${realWidth.toFixed(1)} mm`);
         }
         ph = pdf.internal.pageSize.getHeight();
         bottom = ph - 17;
         y = 27;
         header();
       };
       const ensure = (height) => {
         if (y + height > bottom) {
           // The estimated page dimensions should accommodate each section.
           // Do not silently split a section; a real overflow is surfaced.
           throw new Error(`PDF page height too small: content at ${y.toFixed(1)} mm needs ${height.toFixed(1)} mm; page bottom ${bottom.toFixed(1)} mm`);
         }
       };
      const sectionHeading = (label) => {
        ensure(15);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(11);
        pdf.setTextColor(23, 43, 74);
        pdf.text(label, margin, y + 4);
        pdf.setDrawColor(225, 231, 240);
        pdf.line(margin, y + 7, pw - margin, y + 7);
        y += 14;
      };
      const clipText = (value, maxWidth, fontSize = 8) => {
        pdf.setFontSize(fontSize);
        const label = String(value ?? "—");
        return pdf.getTextWidth(label) <= maxWidth ? label :
          pdf.splitTextToSize(label, maxWidth)[0].replace(/\s+$/, "") + "…";
      };

      header();
      // Print-specific KPI strip; no dashboard DOM screenshot or blank capture pages.
      const kpis = [
        ["Total Finishing Backlog", fmt(summary?.total?.total_backlog), "pcs"],
        ["Capacity / Day", fmt(summary?.total?.capacity_per_day), "pcs"],
        ["Days WIP", fmt(summary?.total?.days_wip_exact), "days"],
        ["Change vs Previous Day", fmt(summary?.total?.change_vs_previous), "pcs"]
      ];
      const cellWidth = contentWidth / 4;
      kpis.forEach(([label, value, unit], i) => {
        const x = margin + i * cellWidth;
        pdf.setFillColor(247, 249, 253);
        pdf.setDrawColor(229, 235, 244);
        pdf.roundedRect(x + 1, y, cellWidth - 3, 23, 2, 2, "FD");
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(97, 114, 138);
        pdf.setFontSize(8);
        pdf.text(label, x + 5, y + 7);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(23, 43, 74);
        pdf.setFontSize(15);
        pdf.text(String(value), x + 5, y + 16);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(7);
        pdf.setTextColor(97, 114, 138);
        pdf.text(unit, x + cellWidth - 8, y + 16, { align: "right" });
      });
      y += 33;

      // Vector bars: all buyers, no scrolling or loss of off-screen data.
      const buyerList = Array.isArray(bars?.buyer_wise?.data) ? bars.buyer_wise.data : [];
      sectionHeading("Buyer-wise Finishing Backlog");
      if (!buyerList.length) {
        pdf.setFontSize(9); pdf.text("No buyer chart data", margin, y + 3); y += 12;
      } else {
        const maxValue = Math.max(1, ...buyerList.map(item => pcs(item.backlog_thousands)));
        const labelWidth = 91;
        const amountWidth = 29;
        const chartX = margin + labelWidth + 5;
        const chartWidth = contentWidth - labelWidth - amountWidth - 12;
        buyerList.forEach((item, index) => {
          ensure(7.6);
          if (index && index % 2 === 1) { pdf.setFillColor(250, 251, 254); pdf.rect(margin, y - 1, contentWidth, 7.1, "F"); }
          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(8);
          pdf.setTextColor(41, 57, 78);
          pdf.text(clipText(item.buyer, labelWidth - 5, 8), margin + 2, y + 3.6);
          const quantity = pcs(item.backlog_thousands);
          pdf.setFillColor(231, 239, 252);
          pdf.roundedRect(chartX, y + 0.7, chartWidth, 4.1, 1.2, 1.2, "F");
          if (quantity > 0) {
            pdf.setFillColor(37, 99, 235);
            pdf.roundedRect(chartX, y + 0.7, Math.max(0.6, quantity / maxValue * chartWidth), 4.1, 1.2, 1.2, "F");
          }
          pdf.setFont("helvetica", "bold");
          pdf.setTextColor(31, 74, 128);
          pdf.text(`${fmt(quantity)}k`, pw - margin - 2, y + 3.6, { align: "right" });
          y += 7.6;
        });
        y += 5;
      }

      // Unit-wise backlog always begins on a new, dedicated PDF page.
      nextPage(unitHeight);
      // Unit bars; compact data-complete chart.
      const unitList = Array.isArray(bars?.unit_wise?.data) ? bars.unit_wise.data : [];
      sectionHeading("Unit-wise Finishing Backlog");
      if (!unitList.length) {
        pdf.setFontSize(9); pdf.text("No unit chart data", margin, y + 3); y += 12;
      } else {
        const maxValue = Math.max(1, ...unitList.map(item => pcs(item.backlog_thousands)));
        const nameWidth = 32;
        const barX = margin + nameWidth + 4;
        // Reserve a separate right-hand value column inside both margins.
        const valueColumnWidth = 31;
        const barWidth = Math.max(20, (pw - margin - valueColumnWidth - 5) - barX);
        unitList.forEach((item, index) => {
          ensure(6.65);
          if (index % 2 === 1) { pdf.setFillColor(250, 251, 254); pdf.rect(margin, y - 1, contentWidth, 6.1, "F"); }
          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(8);
          pdf.setTextColor(41, 57, 78);
          pdf.text(clipText(item.unit, nameWidth - 4), margin + 2, y + 3.6);
          pdf.setFillColor(227, 245, 242);
          pdf.roundedRect(barX, y + 0.7, barWidth, 4.1, 1.2, 1.2, "F");
          const quantity = pcs(item.backlog_thousands);
          if (quantity > 0) {
            pdf.setFillColor(13, 148, 136);
            pdf.roundedRect(barX, y + 0.7, Math.max(0.6, quantity / maxValue * barWidth), 4.1, 1.2, 1.2, "F");
          }
          pdf.setFont("helvetica", "bold");
          pdf.setTextColor(13, 110, 103);
          pdf.text(`${fmt(quantity)}k`, pw - margin - 2, y + 3.6, { align: "right" });
          y += 6.65;
        });
        y += 5;
      }

      // The full Top 3 WIP breakdown uses its own page.
      const pieChartsToPrint = Array.isArray(pies?.charts) ? pies.charts : [];
      const pieSectionHeight = 14 + pieChartsToPrint.reduce((sum, c) => {
        const count = Array.isArray(c.slices) ? c.slices.length : 0;
        return sum + Math.max(36, 17 + Math.ceil(count / 2) * 5.3) + 5;
      }, 0);
      // Start a fresh page when the pies would be stranded at the page bottom.
       nextPage(piePageHeight);
      sectionHeading("Top 3 WIP Units - Buyer-wise Backlog");
      const pieCharts = Array.isArray(pies?.charts) ? pies.charts : [];
      const piePalette = [
        [37, 99, 235], [13, 148, 136], [245, 158, 11], [124, 58, 237],
        [239, 68, 68], [2, 132, 199], [132, 204, 22], [219, 39, 119]
      ];
      pieCharts.forEach((chart) => {
        const slices = Array.isArray(chart.slices) ? chart.slices : [];
        const legendRows = Math.max(1, Math.ceil(slices.length / 2));
        const boxHeight = Math.max(36, 17 + legendRows * 5.3);
        ensure(boxHeight + 3);
        pdf.setDrawColor(230, 236, 245);
        pdf.roundedRect(margin, y, contentWidth, boxHeight, 2, 2, "S");
        pdf.setFont("helvetica", "bold"); pdf.setFontSize(10); pdf.setTextColor(23, 43, 74);
        pdf.text(`Unit ${chart.unit}  |  ${fmt(chart.days_wip)} days WIP  |  ${fmt(chart.total_backlog)} pcs`, margin + 5, y + 8);
        const centerX = margin + 28;
        const centerY = y + boxHeight / 2 + 3;
        const radius = 9;
        const sum = slices.reduce((acc, s) => acc + Math.max(0, pcs(s.backlog_thousands)), 0);
        let angle = -Math.PI / 2;
        slices.forEach((slice, index) => {
          if (sum <= 0) return;
          const piece = Math.max(0, pcs(slice.backlog_thousands)) / sum * Math.PI * 2;
          const [r, g, b] = piePalette[index % piePalette.length];
          pdf.setFillColor(r, g, b);
          const steps = Math.max(1, Math.ceil(piece / 0.12));
          for (let k = 0; k < steps; k++) {
            const a = angle + piece * k / steps;
            const bAngle = angle + piece * (k + 1) / steps;
            pdf.triangle(centerX, centerY, centerX + radius * Math.cos(a), centerY + radius * Math.sin(a), centerX + radius * Math.cos(bAngle), centerY + radius * Math.sin(bAngle), "F");
          }
          angle += piece;
        });
        pdf.setFillColor(255, 255, 255);
        pdf.circle(centerX, centerY, radius * 0.53, "F");
        const firstLegendX = margin + 65;
        const colWidth = (contentWidth - 73) / 2;
        slices.forEach((slice, index) => {
          const column = index % 2;
          const row = Math.floor(index / 2);
          const itemX = firstLegendX + column * colWidth;
          const itemY = y + 16 + row * 5.3;
          const [r, g, b] = piePalette[index % piePalette.length];
          pdf.setFillColor(r, g, b);
          pdf.rect(itemX, itemY - 2.6, 3, 3, "F");
          pdf.setFont("helvetica", "normal"); pdf.setFontSize(7.3); pdf.setTextColor(48, 65, 87);
          pdf.text(clipText(slice.buyer, colWidth - 37, 7.3), itemX + 5, itemY);
          pdf.setFont("helvetica", "bold");
          pdf.text(`${fmt(slice.percent)}%`, itemX + colWidth - 7, itemY, { align: "right" });
        });
        y += boxHeight + 4;
      });

      // Page four: dedicated MIS table; the preceding pie page stays intact.
       nextPage(misPageHeight);
       sectionHeading(reportHeading);
       const tableRowHeight = 8.5;
      // The built-in jsPDF Helvetica font does not contain ▲/▼ glyphs.
      // Draw the arrows as small vector triangles, and use text for the number.
      // Construct the PDF MIS cells from the same live API material formatter used
      // by the dashboard; do not rely on any raw-number intermediate arrays.
      const rows = displayUnits.map((unit) => [
        nameLabel(unit.unit ?? unit.unit_code ?? ""),
        fmt(unit.capacity_per_day),
        materialText(unit.backlog_with_pm, unit),
        materialText(unit.backlog_without_pm, unit),
        materialText(unit.backlog_late_material, unit),
        fmt(unit.total_backlog),
        changeText(unit.change_vs_previous).replace(/[▲▼]/g, ""),
        fmt(unit.days_wip),
        remarksText(unit.error || unit.remarks),
      ]);
      autoTable(pdf, {
        startY: y,
        head: [[
          { content: "Unit No.", rowSpan: 2 },
          { content: "Finishing capacity In Pcs/day", rowSpan: 2 },
          { content: "Finishing Backlog", colSpan: 4, styles: { halign: "center" } },
          { content: "Compare to previous day Backlog Increase /Decrease", rowSpan: 2 },
          { content: "No of days WIP", rowSpan: 2 },
          { content: "Remarks", rowSpan: 2 },
        ], ["With Trims", "Without Trims", "Late Receipt of Trims", "Total"]], body: rows,
        margin: { top: 27, left: margin, right: margin, bottom: 17 },
        styles: { halign: "center", fontSize: 8, cellPadding: 1.25, minCellHeight: tableRowHeight, overflow: "linebreak", valign: "middle", lineColor: [205,205,205], lineWidth: 0.11 },
        headStyles: { fillColor: [31, 78, 121], textColor: [255, 255, 255], fontStyle: "bold", lineColor: [31, 78, 121], lineWidth: 0.14, halign: "center", valign: "middle" },
        columnStyles: { 0: { cellWidth: 24 }, 1: { cellWidth: 48 }, 2: { cellWidth: 48 }, 3: { cellWidth: 48 }, 4: { cellWidth: 51 }, 5: { cellWidth: 36 }, 6: { cellWidth: 57 }, 7: { cellWidth: 29 }, 8: { cellWidth: 53 } },
        theme: "grid",
        tableLineColor: [205,205,205],
        tableLineWidth: 0.12,
        bodyStyles: { fontStyle: "bold", textColor: [25, 42, 68], fillColor: [255,255,255], lineColor: [205,205,205], lineWidth: 0.11 },
        showHead: "everyPage",
        foot: [["Total", fmt(summary?.total?.capacity_per_day), materialText(summary?.total?.backlog_with_pm, summary?.total), materialText(summary?.total?.backlog_without_pm, summary?.total), materialText(summary?.total?.backlog_late_material, summary?.total), fmt(summary?.total?.total_backlog), changeText(summary?.total?.change_vs_previous).replace(/[▲▼]/g, ""), fmt(summary?.total?.days_wip), ""]],
        footStyles: { fillColor: [242, 242, 242], textColor: [23, 43, 74], fontStyle: "bold", lineColor: [180, 180, 180], lineWidth: 0.12 },
        didParseCell: (context) => {
          if ((context.section === "body" || context.section === "foot") && (context.column.index === 2 || context.column.index === 3 || context.column.index === 4)) {
            // Draw quantity and accent-colored percentage separately, centered.
            context.cell.text = [""];
          }
          if ((context.section === "body" || context.section === "foot") && context.column.index === 6) {
            // A single custom renderer draws both the value and the triangle.
            // Prevent autoTable from drawing a separately centered number underneath.
            context.cell.text = [""];
            const amount = context.section === "foot" ? summary?.total?.change_vs_previous : displayUnits[context.row.index]?.change_vs_previous;
            if (Number(amount) > 0) context.cell.styles.textColor = [220, 38, 38];
            else if (Number(amount) < 0) context.cell.styles.textColor = [21, 128, 61];
          }
        },
        didDrawCell: (context) => {
          if (context.section !== "body" && context.section !== "foot") return;
          if (context.column.index === 2 || context.column.index === 3 || context.column.index === 4) {
            const record = context.section === "foot" ? summary?.total : displayUnits[context.row.index];
            const fieldName = context.column.index === 2 ? "backlog_with_pm" : context.column.index === 3 ? "backlog_without_pm" : "backlog_late_material";
            const { quantity, percent } = materialParts(record?.[fieldName], record);
            const fontSize = context.cell.styles.fontSize || 8;
            const gap = percent === null ? 0 : 2.5;
            pdf.setFont("helvetica", "bold");
            pdf.setFontSize(fontSize);
            const qtyWidth = pdf.getTextWidth(quantity);
            let badgeFont = fontSize * 0.88;
            pdf.setFontSize(badgeFont);
            // Fit the quantity + badge as a single centered unit, even for long values.
            const usableWidth = context.cell.width - 3.2;
            let pctWidth = percent === null ? 0 : pdf.getTextWidth(percent);
            let badgeWidth = percent === null ? 0 : pctWidth + 4.4;
            let qtyFont = fontSize;
            let qtyDisplayWidth = qtyWidth;
            if (qtyDisplayWidth + gap + badgeWidth > usableWidth) {
              const scale = Math.max(0.65, usableWidth / (qtyDisplayWidth + gap + badgeWidth));
              qtyFont = fontSize * scale;
              badgeFont = fontSize * 0.88 * scale;
              pdf.setFontSize(qtyFont);
              qtyDisplayWidth = pdf.getTextWidth(quantity);
              pdf.setFontSize(badgeFont);
              pctWidth = percent === null ? 0 : pdf.getTextWidth(percent);
              badgeWidth = percent === null ? 0 : pctWidth + 3.8;
            }
            const totalWidth = qtyDisplayWidth + gap + badgeWidth;
            const leftX = context.cell.x + Math.max(1.6, (context.cell.width - totalWidth) / 2);
            const baseline = context.cell.y + context.cell.height / 2 + fontSize * 0.12;
            pdf.setFontSize(qtyFont);
            pdf.setTextColor(25, 42, 68);
            pdf.text(quantity, leftX, baseline);
            if (percent !== null) {
              const tone = materialBadgeTone(percent);
              const palette = tone === "high"
                ? { bg: [220, 252, 231], fg: [22, 101, 52] }
                : tone === "medium"
                  ? { bg: [254, 243, 199], fg: [146, 64, 14] }
                  : { bg: [254, 226, 226], fg: [153, 27, 27] };
              const badgeHeight = Math.min(5.9, context.cell.height - 2);
              const badgeX = leftX + qtyDisplayWidth + gap;
              const badgeY = context.cell.y + (context.cell.height - badgeHeight) / 2;
              pdf.setFillColor(...palette.bg);
              pdf.roundedRect(badgeX, badgeY, badgeWidth, badgeHeight, 2.7, 2.7, "F");
              pdf.setFontSize(badgeFont);
              pdf.setTextColor(...palette.fg);
              pdf.text(percent, badgeX + badgeWidth / 2, badgeY + badgeHeight / 2 + badgeFont * 0.11, { align: "center" });
            }
            return;
          }
          if (context.column.index !== 6) return;
          const amount = Number(context.section === "foot" ? summary?.total?.change_vs_previous : displayUnits[context.row.index]?.change_vs_previous);
          if (!Number.isFinite(amount) || amount === 0) {
            pdf.setFont("helvetica", "bold");
            pdf.setFontSize(context.cell.styles.fontSize || 8);
            pdf.setTextColor(25, 42, 68);
            pdf.text(Number.isFinite(amount) ? "0" : "—", context.cell.x + context.cell.width / 2,
              context.cell.y + context.cell.height / 2 + (context.cell.styles.fontSize || 8) * 0.13,
              { align: "center" });
            return;
          }
          // Draw BOTH glyphs ourselves: autoTable's centered text would otherwise
          // overlap a triangle positioned relative to another center point.
          const plainNumber = fmt(amount);
          const cell = context.cell;
          const fontSize = cell.styles.fontSize || 8;
          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(fontSize);
          const numberWidth = pdf.getTextWidth(plainNumber);
          const triangleHalf = 1.25;
          const arrowGap = 2.9;
          const groupWidth = numberWidth + arrowGap + triangleHalf * 2;
          const left = cell.x + (cell.width - groupWidth) / 2;
          const centerY = cell.y + cell.height / 2;
          const baseline = centerY + fontSize * 0.13;
          const rgb = amount > 0 ? [220, 38, 38] : [21, 128, 61];
          pdf.setTextColor(...rgb);
          pdf.text(plainNumber, left, baseline);
          const arrowCenter = left + numberWidth + arrowGap + triangleHalf;
          pdf.setFillColor(...rgb);
          if (amount > 0) {
            pdf.triangle(arrowCenter - triangleHalf, centerY + 1.3,
              arrowCenter + triangleHalf, centerY + 1.3,
              arrowCenter, centerY - 1.3, "F");
          } else {
            pdf.triangle(arrowCenter - triangleHalf, centerY - 1.3,
              arrowCenter + triangleHalf, centerY - 1.3,
              arrowCenter, centerY + 1.3, "F");
          }
        },
        didDrawPage: () => { /* Each explicit page already has a header. */ }
      });
      // Guard against accidental automatic table pagination, which can create
      // visually clipped rows or an unexpected fifth page.
      const pages = pdf.internal.getNumberOfPages();
      if (pages !== 4) throw new Error(`PDF pagination mismatch: expected 4 sections, generated ${pages} pages. Reduce font or increase the MIS page estimate.`);
      for (let i = 1; i <= pages; i++) {
        pdf.setPage(i);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(125, 138, 155);
        pdf.setFontSize(8);
        pdf.text(`${i} / ${pages}`, pdf.internal.pageSize.getWidth() - margin, pdf.internal.pageSize.getHeight() - 8, { align: "right" });
      }
      return pdf;
  };

  const exportPdf = async () => {
    if (!summary) return message.warning("Dashboard data is not ready");
    try {
      setExporting(true);
      const pdf = await buildReportPdf();
      pdf.save(`Finishing_Backlog_Dashboard_${date}.pdf`);
    } catch (err) {
      console.error(err);
      message.error(err?.message || "PDF export failed");
    } finally {
      setExporting(false);
    }
  };

  const sendReportMail = async () => {
    if (sendingMail || exporting || loading) return;
    if (!summary) return message.warning("Dashboard data is not ready");
    const userId = reportUserId(userdata);
    if (!userId) {
      return message.error("Logged-in user ID not found in userdata.");
    }
    try {
      setSendingMail(true);
      const pdf = await buildReportPdf();
      const pdfBlob = pdf.output("blob");
      if (pdfBlob.size > 10 * 1024 * 1024) {
        throw new Error("PDF is larger than 10 MB. Cannot send report mail.");
      }
      const formData = new FormData();
      formData.append("pdf", pdfBlob, `Finishing_Backlog_Report_${date}.pdf`);
      formData.append("user_id", userId);
      formData.append("date", date);
      formData.append("include_summary", "1");

      // The backend reads recipients exclusively from the central mails table.
      // Do not specify Content-Type: fetch sets the multipart boundary automatically.
      const response = await fetch(new URL("send_report_mail", API_ROOT).toString(), {
        method: "POST",
        body: formData,
      });
      const result = await response.json().catch(() => null);
      if (!response.ok || result?.status !== true) {
        throw new Error(result?.message || `Mail request failed (HTTP ${response.status})`);
      }
      message.success(result?.message || "Mail sent successfully");
    } catch (err) {
      console.error("Finishing backlog report mail error:", err);
      message.error(err?.message || "Failed to send report mail");
    } finally {
      setSendingMail(false);
    }
  };
  const total = summary?.total || {};
  const stats = [{ label: "Total Finishing Backlog", value: fmt(total.total_backlog), suffix: "pcs" }, { label: "Capacity / Day", value: fmt(total.capacity_per_day), suffix: "pcs" }, { label: "Days WIP", value: fmt(total.days_wip_exact), suffix: "days" }, { label: "Change vs Previous Day", value: fmt(total.change_vs_previous), suffix: total.trend === "up" ? "Increase" : total.trend === "down" ? "Decrease" : "pcs" }];
  return <div className="sm-page fba-page unified-report-page fba-mis-dashboard"><div className="sm-page-inner"><section className="unified-panel">
    <header className="unified-heading"><div><Typography.Title level={3} className="sm-page-title"><BarChartOutlined /> Finishing Backlog Analytics</Typography.Title></div><div className="fba-export-actions"><Button icon={<FileExcelOutlined />} loading={exporting} onClick={exportExcel}>Excel</Button><Button icon={<FilePdfOutlined />} disabled={sendingMail} loading={exporting} onClick={exportPdf}>PDF</Button><Button icon={<MailOutlined />} disabled={exporting || loading || !summary} loading={sendingMail} onClick={sendReportMail}>Send Mail</Button></div></header>
    {!!errors.length && <Alert type="warning" showIcon className="fba-alert" message="Some analytics could not be loaded" description={errors.join(" | ")} />}
    <Spin spinning={loading} tip="Loading analytics..."><div className="fba-main" ref={dashboardRef}>
      <div className="fba-kpi-grid fba-pdf-section" data-pdf-section="kpis">{stats.map(s => <div className="fba-kpi" key={s.label}><span>{s.label}</span><strong>{summary ? s.value : "—"}</strong><small>{s.suffix}</small></div>)}</div>
      <div className="fba-bar-grid"><div className="fba-pdf-section" data-pdf-section="buyers"><HorizontalBuyerBarPanel data={bars?.buyer_wise?.data} /></div><div className="fba-pdf-section" data-pdf-section="units"><VerticalBarPanel title="Unit-wise Finishing Backlog" data={bars?.unit_wise?.data} labelKey="unit" color="#0d9488" /></div></div>
      <div className="fba-pdf-section" data-pdf-section="pies"><div className="fba-section-title"><strong>Top 3 WIP Units — Buyer-wise Backlog</strong></div>
      <div className="fba-pie-grid">{Array.isArray(pies?.charts) && pies.charts.length ? pies.charts.map((chart, i) => <BuyerPiePanel key={`${chart.unit_code || chart.unit}-${i}`} chart={chart} />) : <div className="fba-empty"><Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No WIP pie data" /></div>}</div></div>
      <section className="fba-mis-report-section"><div className="fba-section-title fba-mis-report-title"><strong>{reportHeading}</strong></div>
      <div className="unified-toolbar fba-report-toolbar"><Input className="unified-search" allowClear prefix={<SearchOutlined />} placeholder="Search unit or remarks" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} /><Select className="fba-trend-filter" value={trendFilter} onChange={v => { setTrendFilter(v); setPage(1); }} options={[{ value:"all", label:"All Trends" }, { value:"up", label:"Increased" }, { value:"down", label:"Decreased" }, { value:"same", label:"Unchanged" }]} /></div>
      <Table className="unified-table fba-table fba-excel-reference-table" dataSource={filtered} rowKey={(r, i) => String(r.unit_code || r.unit || i)} columns={columns} size="small" scroll={{ x: 1640 }} pagination={false} summary={() => filtered.length && trendFilter === "all" && !search ? <Table.Summary.Row>{["Total", fmt(total.capacity_per_day), materialDisplay(total.backlog_with_pm, total), materialDisplay(total.backlog_without_pm, total), materialDisplay(total.backlog_late_material, total), fmt(total.total_backlog), changeText(total.change_vs_previous), fmt(total.days_wip), ""].map((value, i) => <Table.Summary.Cell index={i} key={i} align="center"><strong style={i === 6 ? {color: changeColor(total.change_vs_previous)} : undefined}>{value}</strong></Table.Summary.Cell>)}</Table.Summary.Row> : null} />
    </section></div></Spin>
  </section></div></div>;
}
